import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getEvidence } from "@/api";
import { EVIDENCE_RECORD } from "@/constants/testIds";
import AsciiDivider from "@/components/AsciiDivider";

const formatRelative = (iso) => {
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch {
    return "—";
  }
};

const formatAbsolute = (iso) => {
  try {
    return new Date(iso).toUTCString().replace(" GMT", " UTC");
  } catch {
    return iso;
  }
};

const EvidenceRecord = () => {
  const { hash } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await getEvidence(hash);
      setCard(data);
    } catch (e) {
      setErr(
        e?.response?.status === 404
          ? "Evidence record not found."
          : "Could not load evidence record."
      );
    } finally {
      setLoading(false);
    }
  }, [hash]);

  useEffect(() => {
    load();
  }, [load]);

  const shareUrl = `${window.location.origin}/evidence/${hash}`;

  const copyShare = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      try {
        const ta = document.createElement("textarea");
        ta.value = shareUrl;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        // both clipboard APIs failed (browser blocked / sandboxed iframe);
        // visual feedback still fires below so the user knows the click registered.
        console.warn("clipboard copy fallback failed");
      }
    }
    // Always surface visual feedback so the user knows the click registered.
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="term-container">
        <p className="muted">$ loading evidence record<span className="cursor" /></p>
      </div>
    );
  }

  if (err || !card) {
    return (
      <div className="term-container fade-in" data-testid={EVIDENCE_RECORD.root}>
        <div className="kbd-label">// evidence</div>
        <h1 style={{ margin: "0.2rem 0 1rem", color: "var(--ax-danger)" }}>
          [ NOT FOUND ]
        </h1>
        <p className="muted">{err || "Unknown error."}</p>
        <div style={{ marginTop: "1rem" }}>
          <button onClick={() => navigate("/")}>← BACK TO ARCHIVE</button>
        </div>
      </div>
    );
  }

  const status = card.status || "pending";
  const STATUS_META = {
    promoted: {
      label: "PROMOTED BY CELL VOTE",
      pill: "active",
      explainer:
        "This piece of evidence was elevated to the dossier by community voting.",
    },
    rising: {
      label: "RISING — under community review",
      pill: "active",
      explainer:
        "This submission is gaining net upvotes and is approaching promotion.",
    },
    flagged: {
      label: "UNDER REVIEW — flagged by multiple users",
      pill: "warn",
      explainer:
        "Multiple users have flagged this submission. It remains visible while the cell reviews.",
    },
    pending: {
      label: "PENDING — awaiting community verdict",
      pill: "",
      explainer:
        "Pending community review. Promoted once it clears the vote and flag thresholds.",
    },
  };
  const meta = STATUS_META[status] || STATUS_META.pending;
  const slugUpper = (card.theory_slug || "").toUpperCase().replace(/-/g, " ");
  const typeUpper = (card.type || "").toUpperCase();
  const short8 = card.hash.slice(0, 8);
  const short6Tail = card.hash.slice(-6);
  const net = card.upvotes - card.downvotes;

  return (
    <div
      className="term-container fade-in"
      data-testid={EVIDENCE_RECORD.root}
      style={{ maxWidth: 820 }}
    >
      <div
        data-testid={EVIDENCE_RECORD.header}
        className="kbd-label"
        style={{ marginBottom: "0.3rem" }}
      >
        // EVIDENCE RECORD · 0x{short8}…{short6Tail}
      </div>

      <div
        data-testid={EVIDENCE_RECORD.classification}
        style={{
          color: "var(--ax-accent)",
          letterSpacing: "0.12em",
          fontSize: 13,
          textTransform: "uppercase",
        }}
      >
        AXIOM COMMUNITY SUBMISSION · {typeUpper} EVIDENCE
      </div>

      <div
        data-testid={EVIDENCE_RECORD.dossier}
        className="muted"
        style={{
          letterSpacing: "0.1em",
          fontSize: 13,
          textTransform: "uppercase",
          marginTop: "0.2rem",
        }}
      >
        RE: {slugUpper} DOSSIER
      </div>

      <AsciiDivider char="=" />

      <div className="evidence-record-card">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.65rem",
            flexWrap: "wrap",
            marginBottom: "0.8rem",
          }}
        >
          <span
            data-testid={EVIDENCE_RECORD.statusBadge}
            className={`pill ${meta.pill}`}
          >
            {meta.label}
          </span>
          <span
            data-testid={EVIDENCE_RECORD.voteDisplay}
            className="muted"
            style={{ fontSize: 13 }}
          >
            {card.upvotes}↑ / {card.downvotes}↓ ·{" "}
            <span
              className="glow"
              style={{ fontWeight: 700 }}
            >
              NET: {net >= 0 ? "+" : ""}
              {net}
            </span>
          </span>
          <span
            data-testid={EVIDENCE_RECORD.submittedAt}
            className="faint"
            style={{ fontSize: 12, marginLeft: "auto" }}
            title={formatAbsolute(card.submitted_at)}
          >
            submitted {formatRelative(card.submitted_at)} by{" "}
            <span className="muted">{card.submitted_by}</span>
          </span>
        </div>

        <div
          className="faint"
          style={{
            fontSize: 12,
            margin: "0 0 0.6rem",
            letterSpacing: "0.02em",
          }}
        >
          {meta.explainer}
        </div>

        <div
          data-testid={EVIDENCE_RECORD.content}
          style={{
            fontSize: 15,
            lineHeight: 1.6,
            whiteSpace: "pre-wrap",
            padding: "0.4rem 0",
            borderTop: "1px dashed var(--ax-border-bright)",
            borderBottom: "1px dashed var(--ax-border-bright)",
            margin: "0.4rem 0 0.8rem",
            color: "var(--ax-text)",
          }}
        >
          {card.content}
        </div>

        {card.source_url && (
          <div style={{ marginTop: "0.3rem" }}>
            <a
              data-testid={EVIDENCE_RECORD.source}
              href={card.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="evidence-source"
              style={{ fontSize: 13 }}
            >
              → external source
            </a>
          </div>
        )}
      </div>

      <AsciiDivider />

      <div
        style={{
          display: "flex",
          gap: "0.7rem",
          flexWrap: "wrap",
          margin: "1rem 0",
        }}
      >
        <Link
          to={`/theories/${card.theory_slug}`}
          data-testid={EVIDENCE_RECORD.viewDossierCta}
          style={{ borderBottom: "none" }}
        >
          <button>→ VIEW FULL DOSSIER</button>
        </Link>
        <Link
          to={`/theories/${card.theory_slug}#evidence`}
          data-testid={EVIDENCE_RECORD.submitOwnCta}
          style={{ borderBottom: "none" }}
        >
          <button className="ghost">→ SUBMIT YOUR OWN EVIDENCE</button>
        </Link>
      </div>

      <div className="evidence-record-share" data-testid={EVIDENCE_RECORD.shareLine}>
        <span className="muted" style={{ fontSize: 11, letterSpacing: "0.12em" }}>
          SHARE THIS RECORD:
        </span>
        <code className="record-url">{shareUrl}</code>
        <button
          data-testid={EVIDENCE_RECORD.copyShare}
          onClick={copyShare}
          className="ghost"
          style={{
            padding: "0.2rem 0.6rem",
            fontSize: 11,
            minWidth: 110,
          }}
        >
          {copied ? "LINK COPIED" : "COPY"}
        </button>
      </div>
    </div>
  );
};

export default EvidenceRecord;
